import React, { useState, useEffect } from "react";
import { RawFruitService } from "../services/RawFruitService";
import { FarmerService, Farmer } from "../services/FarmerService";
import { MachineService, Machine } from "../services/MachineService";
import "./RawFruit.css";



const RawFruitPage: React.FC = () => {
  const [rawFruits, setRawFruits] = useState<any[]>([]);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [loading, setLoading] = useState(true);
  const [formSubmitting, setFormSubmitting] = useState(false);

  // Validation errors
  const [formErrors, setFormErrors] = useState<{
    fruit?: string;
    weight?: string;
    remarks?: string;
    farmerId?: string;
    machineId?: string;
  }>({});

  // NEW: Farmers & Machines state
  const [farmers, setFarmers] = useState<Farmer[]>([]);
  const [machines, setMachines] = useState<Machine[]>([]);

  // Fetch raw fruits
  const fetchRawFruits = async () => {
    try {
      setLoading(true);
      const data = await RawFruitService.fetchRawFruits();
      setRawFruits(data.data);
    } catch (error) {
      console.error("Error fetching raw fruits:", error);
    } finally {
      setLoading(false);
    }
  };

  // NEW: Fetch all farmers & machines
  const fetchFarmersAndMachines = async () => {
    try {
      const [farmersRes, machinesRes] = await Promise.all([
        FarmerService.fetchFarmers(),
        MachineService.fetchMachines(),
      ]);
      setFarmers(farmersRes.data);
      setMachines(machinesRes.data);
    } catch (err) {
      console.error("Error fetching farmers/machines:", err);
    }
  };

  // Start production
  const handleStartProduction = async (id: string) => {
    try {
      await RawFruitService.startProduction(id);
      fetchRawFruits();
    } catch (error) {
      console.error("Error starting production:", error);
    }
  };

  // Submit form
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormSubmitting(true);

    const formData = new FormData(e.currentTarget);

    const data = {
      fruit: formData.get("fruit") as string,
      weight: parseInt(formData.get("weight") as string),
      remarks: formData.get("remarks") as string,
      farmerId: formData.get("farmerId") as string,
      machineId: formData.get("machineId") as string,
    };

    // Basic validation
    const errors: {
      fruit?: string;
      weight?: string;
      farm?: string;
      farmerId?: string;
      machineId?: string;
    } = {};

    if (!data.fruit) errors.fruit = "Fruit is required";
    if (!data.weight || data.weight <= 0)
      errors.weight = "Weight must be greater than 0";
    if (!data.farmerId) errors.farmerId = "Please select a farmer";
    if (!data.machineId) errors.machineId = "Please select a machine";

    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      setFormSubmitting(false);
      return;
    }

    try {
      await RawFruitService.submitRawFruit(data);
      alert("Raw fruit data submitted successfully!");

      e.currentTarget.reset();
    } catch (error) {
      console.error("Error submitting raw fruit data:", error);
    } finally {
      setIsFormVisible(false);
      setFormSubmitting(false);
      fetchRawFruits();
    }
  };

  // On mount, fetch existing raw fruits + all farmers & machines
  useEffect(() => {
    fetchRawFruits();
    fetchFarmersAndMachines();
  }, []);

  return (
    <div className="page">
      <div className="container">
        <div className="header">
          <button
            className="button"
            style={{ position: "absolute", top: "0px", right: "10px" }}
            onClick={() => setIsFormVisible(true)}
          >
            Add Raw Fruit
          </button>
        </div>

        {loading ? (
          <p>Loading...</p>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Fruit</th>
                <th>Weight (kg)</th>
                <th>Remarks</th>
                <th>Farmer</th>
                <th>Machine</th>

                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {rawFruits.map((fruit, index) => (
                <tr key={index}>
                  <td>{fruit.fruit}</td>
                  <td>
                    {fruit.weight ? fruit.weight.toFixed(2) : fruit.weight}
                  </td>
                  <td>{fruit.remarks}</td>

                  {/* If the backend returns a 'farmer' object, show name. Otherwise show farmerId */}
                  <td>
                    {fruit.farmer?.name
                      ? fruit.farmer.name
                      : fruit.farmerId || "N/A"}
                  </td>

                  {/* Same logic for machine */}
                  <td>
                    {fruit.machine?.owner
                      ? fruit.machine.owner
                      : fruit.machineId || "N/A"}
                  </td>

                  <td>
                    {fruit.startedAt && fruit.stoppedAt
                      ? "Production Completed"
                      : fruit.startedAt
                      ? "In Production"
                      : "Pending"}
                  </td>
                  <td>
                    {!fruit.startedAt && !fruit.stoppedAt && (
                      <button
                        className="button start-button"
                        onClick={() => handleStartProduction(fruit.id)}
                      >
                        Start Production
                      </button>
                    )}
                    {fruit.startedAt && !fruit.stoppedAt && (
                      <span className="in-production">In Production</span>
                    )}
                    {fruit.startedAt && fruit.stoppedAt && (
                      <span className="completed">Completed</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {isFormVisible && (
          <div className="form-popup">
            <form onSubmit={handleSubmit} className="form">
              <h2 className="title">Add Raw Fruit</h2>

              {/* Fruit Field */}
              <label className="label">Fruit:</label>
              <input
                type="text"
                name="fruit"
                className="input"
                placeholder="Enter fruit name"
              />
              {formErrors.fruit && <p className="error">{formErrors.fruit}</p>}

              {/* Weight Field */}
              <label className="label">Weight (kg):</label>
              <input
                type="number"
                step="0.01"
                name="weight"
                className="input"
                placeholder="Enter weight"
              />
              {formErrors.weight && (
                <p className="error">{formErrors.weight}</p>
              )}

              {/* Remarks */}
              <label className="label">Remarks:</label>
              <textarea
                name="remarks"
                className="textarea"
                placeholder="Add any remarks"
              ></textarea>

              {/* NEW: Farmer dropdown */}
              <label className="label">Farmer:</label>
              <select name="farmerId" className="input">
                <option value="">-- Select Farmer --</option>
                {farmers.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name} (ID: {f.id})
                  </option>
                ))}
              </select>
              {formErrors.farmerId && (
                <p className="error">{formErrors.farmerId}</p>
              )}

              {/* NEW: Machine dropdown */}
              <label className="label">Machine:</label>
              <select name="machineId" className="input">
                <option value="">-- Select Machine --</option>
                {machines.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.owner} (ID: {m.id})
                  </option>
                ))}
              </select>
              {formErrors.machineId && (
                <p className="error">{formErrors.machineId}</p>
              )}

              {/* Submit & Cancel */}
              <button type="submit" className="button" disabled={formSubmitting}>
                {formSubmitting ? "Submitting..." : "Submit"}
              </button>
              <button
                type="button"
                className="button cancel"
                onClick={() => setIsFormVisible(false)}
              >
                Cancel
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default RawFruitPage;
